/**
 * TypeScript types for analytics events and dashboard.
 */

// =============================================================================
// Event Tracking
// =============================================================================

export const AnalyticsEventType = {
  PAGE_VIEW: 'page_view',
  SCORECARD_VIEW: 'scorecard_view',
  PROFILE_VIEW: 'profile_view',
  SEARCH: 'search',
  UPLOAD_STARTED: 'upload_started',
  UPLOAD_COMPLETED: 'upload_completed',
  LIKE: 'like',
  UNLIKE: 'unlike',
  COMMENT: 'comment',
  REACTION: 'reaction',
  SHARE_CLICK: 'share_click',
  FOLLOW: 'follow',
  UNFOLLOW: 'unfollow',
  FEED_SCROLL: 'feed_scroll',
  FEATURE_DISCOVERY: 'feature_discovery',
  SIGNUP: 'signup',
  LOGIN: 'login',
} as const;

export type EventType = (typeof AnalyticsEventType)[keyof typeof AnalyticsEventType];

export interface AnalyticsEventCreate {
  event_type: EventType;
  event_subtype?: string;
  session_id: string;
  page_path?: string;
  referrer_path?: string;
  target_type?: string;
  target_id?: number;
  event_data?: Record<string, unknown>;
  device_type?: 'desktop' | 'mobile' | 'tablet';
}

export interface AnalyticsEventResponse {
  status: string;
  event_id: number;
}

// =============================================================================
// Dashboard Metrics
// =============================================================================

export interface TimeRangeMetric {
  all_time: number;
  last_1h: number;
  last_24h: number;
  last_30: number;
}

export interface TrafficTrendPoint {
  bucket: string;
  page_views: number;
  sessions: number;
  visitors: number;
}

export interface ReferrerStats {
  referrer: string;
  count: number;
  unique_sessions: number;
}

export interface EventTypeCount {
  event_type: string;
  count: number;
  pct: number;
}

export interface PageViewStats {
  page_path: string;
  view_count: number;
  unique_users: number;
  unique_sessions: number;
}

export interface FunnelStep {
  step: string;
  count: number;
  conversion_rate: number;
}

export interface FeatureUsage {
  feature: string;
  user_count: number;
  event_count: number;
  adoption_rate: number;
}

export interface SessionStats {
  total_sessions: TimeRangeMetric;
  avg_events_per_session: number;
  avg_session_duration_minutes: number | null;
  bounce_rate: number;
}

export interface SearchStats {
  total_searches: TimeRangeMetric;
  top_search_terms: { query: string; count: number }[];
  searches_with_results: number;
  searches_without_results: number;
}

export interface NavigationPath {
  path: string[];
  count: number;
  pct: number;
}

export interface AnalyticsOverviewResponse {
  // Hero metrics
  total_events: TimeRangeMetric;
  unique_visitors: TimeRangeMetric;
  unique_sessions: TimeRangeMetric;
  page_views: TimeRangeMetric;

  // Breakdowns
  events_by_type: EventTypeCount[];
  top_pages: PageViewStats[];
  funnel: FunnelStep[];
  feature_usage: FeatureUsage[];
  session_stats: SessionStats;
  search_stats: SearchStats;
  common_paths: NavigationPath[];

  // Traffic trends
  traffic_trend_hourly: TrafficTrendPoint[];
  traffic_trend_daily: TrafficTrendPoint[];

  // External referrers
  top_referrers: ReferrerStats[];

  // Timestamp
  generated_at: string;
}
